<template>
  <div class="d-flex align-center">
    <!-- AVATAR AUTEUR -->
    <v-avatar
      :color="authorData.avatar ? '' : 'primary'"
      :class="authorData.avatar ? '' : 'v-avatar-light-bg primary--text'"
      size="32"
    >
      <v-img
        v-if="authorData.avatar"
        :lazy-src="require(`@/assets/images/avatars/2.png`)"
        :src="authorData.avatar || require(`@/assets/images/avatars/2.png`)"
      ></v-img>
      <span
        v-else
        class="font-weight-medium"
      >{{ avatarText(authorData.full_name) }}</span>
    </v-avatar>

    <div class="d-flex flex-column ms-3">
      <div class="d-flex align-center">
        <router-link
          v-if="authorData.id || authorData.author_id"
          :to="{ name: 'author-view', params: { id: authorData.id || authorData.author_id } }"
          class="text--primary font-weight-semibold text-truncate cursor-pointer text-decoration-none"
        >
          {{ authorData.full_name }}
          <span
            v-if="isDp"
            title="Dossier prioritaire"
            class="font-weight-bold ml-1"
          >
            <v-avatar
              color="primary"
              size="16"
            >
              <small class="white--text">DP</small>
            </v-avatar>
          </span>
        </router-link>
        <span
          v-else
          class="text--primary font-weight-semibold"
        >
          {{ authorData.full_name || '-' }}
        </span>

        <!-- BOUTON ET POPUP DU MENU SYNOPTIQUE -->
        <!-- ICONE ET DIALOG (Affichés uniquement si showIcon est true) -->
        <template v-if="showIcon">
          <v-btn
            icon
            x-small
            color="primary"
            class="ml-1"
            @click="isMenuOpen = true; onMenuToggle(true)"
          >
            <v-icon size="18">
              {{ icons.mdiInformationOutline }}
            </v-icon>
          </v-btn>

          <!-- POPUP MODALE CENTRÉE DE 80% -->
          <v-dialog
            v-model="isMenuOpen"
            :width="$vuetify.breakpoint.smAndDown ? '95vw' : '80vw'"
            max-width="1200px"
            scrollable
          >
            <v-card class="pa-2 d-flex flex-column" style="max-height: 85vh;">
              <v-card-title class="text-subtitle-1 font-weight-bold py-2 d-flex justify-space-between align-center flex-shrink-0">
                <div class="font-weight-bold text-h5 py-2 text-truncate">
                  <span class="text-truncate">
                    {{ $t('Fiche synoptique de') }}  {{ (fullAuthorDetails && fullAuthorDetails.full_name) ? fullAuthorDetails.full_name : authorData.full_name }}
                  </span>

                  <!-- BOUTON 1 : Editer info auteur (si permission) -->
                  <v-btn
                    v-if="$can('edit', 'Author') && (authorData.id || authorData.author_id)"
                    :to="{ name: 'author-info', params: { id: authorData.id || authorData.author_id } }"
                    target="_blank"
                    text
                    small
                    color="primary"
                    class="ml-15 text-none"
                  >
                    <v-icon size="18" class="mr-1">
                      {{ icons.mdiFileDocumentEditOutline }}
                    </v-icon>
                    {{ $t("Ajouter l'info de l'auteur") }}
                  </v-btn>

                  <!-- BOUTON 2 : Fiche synoptique complète -->
                  <v-btn
                    v-if="authorData.id || authorData.author_id"
                    :to="{ name: 'author-details', params: { id: authorData.id || authorData.author_id } }"
                    target="_blank"
                    text
                    small
                    color="primary"
                    class="ml-1 text-none"
                  >
                    <v-icon size="18" class="mr-1">
                      {{ icons.mdiChartBoxOutline }}
                    </v-icon>
                    {{ $t('Fiche Synoptique') }}
                  </v-btn>
                </div>
                <v-btn
                  icon
                  small
                  @click="isMenuOpen = false"
                >

                  <v-icon small>
                    {{ icons.mdiClose }}
                  </v-icon>
                </v-btn>
              </v-card-title>
              <v-divider class="flex-shrink-0" />

              <!-- LOADER PENDANT LA REQUÊTE HTTP -->
              <v-progress-linear
                v-if="loadingAuthor"
                indeterminate
                color="primary"
              />

              <!-- AFFICHAGE DES DONNÉES AUTEUR -->
              <v-card-text
                v-else
                class="pa-4 overflow-y-auto"
              >
                <author-overview-icone :author="fullAuthorDetails || authorData"/>
              </v-card-text>
            </v-card>
          </v-dialog>
        </template>
      </div>

      <!-- BARRE DE PROGRESSION (EN DESSOUS DU NOM ET DE L'ICÔNE) -->
      <template v-if="showProgressInfo">
        <div
          class="d-flex align-center mt-1"
          style="width: 100px"
        >
          <v-progress-linear
            :value="authorData.progress || 0"
            :color="getColor()"
            show-label
            rounded
            striped
          >
          </v-progress-linear>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  mdiFileDocumentEditOutline,
  mdiChartBoxOutline,
  mdiClipboardTextOutline,
  mdiClose,
  mdiInformationOutline,
} from '@mdi/js'
import {computed, ref} from '@vue/composition-api'
import {avatarText} from '@core/utils/filter'
import axios from '@axios'
import AuthorOverviewIcone from '@/views/apps/author/author-view/components/AuthorOverviewIcone.vue'

export default {
  name: 'AuthorWidget',
  components: {AuthorOverviewIcone},
  props: {
    object: {
      type: Object,
      default: () => ({}),
    },
    showCivility: {
      type: Boolean,
      default: true,
    },
    isDossierPrioritaire: {
      type: Number,
      default: 0,
    },
    showProgressInfo: {
      type: Boolean,
      default: false,
    },
    showIcon: {
      type: Boolean,
      default: false, // Masqué par défaut
    },
  },
  setup(props, {emit}) {
    const authorData = computed({
      get: () => props.object || {},
      set: value => emit('update:object', value),
    })

    const isMenuOpen = ref(false)
    const loadingAuthor = ref(false)
    const fullAuthorDetails = ref(null)

    const fetchAuthorData = async () => {
      const authorId = authorData.value?.id || authorData.value?.author_id
      if (!authorId || loadingAuthor.value) return

      loadingAuthor.value = true
      try {
        const response = await axios.get(`/authors/${authorId}`)
        fullAuthorDetails.value = response.data
      } catch (error) {
        console.error('Erreur lors de la récupération des détails de l\'auteur :', error)
      } finally {
        loadingAuthor.value = false
      }
    }

    const onMenuToggle = isOpen => {
      if (isOpen && !fullAuthorDetails.value) {
        fetchAuthorData()
      }
    }

    const isDp = computed(() => props.isDossierPrioritaire === 1)

    const getColor = () => {
      const {progress} = authorData.value
      if (!progress) return 'error'
      if (progress < 25) return 'error'
      if (progress <= 75) return 'warning'

      return 'success'
    }

    return {
      authorData,
      isDp,
      getColor,
      avatarText,
      isMenuOpen,
      loadingAuthor,
      fullAuthorDetails,
      onMenuToggle,
      icons: {
        mdiFileDocumentEditOutline,
        mdiChartBoxOutline,
        mdiClipboardTextOutline,
        mdiClose,
        mdiInformationOutline,
      },
    }
  },
}
</script>
